import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

/**
 * Fetches installed module providers from the backend.
 * Returns { installation, providers, customModules, isInstalled, loading, error, refetch, healthStatus, getHealthStatus }.
 *
 * Issue #151 Phase 2: also fetches provider health status every 60s.
 *
 * Custom modules (plan Phase 7) are fetched here too, from the sibling endpoint
 * `/v1/modules/custom`. Two lists rather than one merged list, deliberately: a provider
 * is an external service in an iframe and a custom module is an in-process bundle the
 * shell mounts directly, so they render through different paths and the components
 * downstream have to tell them apart. Fetched from the same hook because this is already
 * the shell's single module-state fetch point — a second hook would mean a second poll
 * and two loading flags for one visual state.
 */
export function useModuleInstallation(providerId) {
    const { getAuthHeader, loading: authLoading } = useAuth();
    const [providers, setProviders] = useState([]);
    const [customModules, setCustomModules] = useState([]);
    const [healthStatus, setHealthStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProviders = useCallback(async () => {
        try {
            setLoading(true);
            // Fetched together so `loading` describes both lists. Settled rather than
            // all-or-nothing: a runtime whose custom-module endpoint fails should still
            // render its provider workspaces, and vice versa — one module system being
            // unavailable is not a reason to show an empty rail.
            const [providerResult, customResult] = await Promise.allSettled([
                apiFetch('/v1/modules/providers', getAuthHeader()),
                apiFetch('/v1/modules/custom', getAuthHeader()),
            ]);

            if (providerResult.status === 'fulfilled') {
                const data = providerResult.value;
                setProviders(Array.isArray(data) ? data : data.providers || []);
            } else {
                setError(providerResult.reason?.message || 'Could not load module providers');
            }

            if (customResult.status === 'fulfilled') {
                setCustomModules(customResult.value?.modules || []);
            } else {
                // Not surfaced through `error`: on a runtime image that predates the
                // loader this endpoint legitimately 404s, and turning that into a visible
                // error would make every older runtime look broken.
                setCustomModules([]);
            }
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    const fetchHealth = useCallback(async () => {
        try {
            const data = await apiFetch('/v1/modules/providers/health', getAuthHeader());
            setHealthStatus(data.providers || {});
        } catch (err) {
            // Silent fail — health status is non-critical
        }
    }, [getAuthHeader]);

    useEffect(() => {
        // Don't fetch until auth has finished loading — avoids 401 console
        // errors on initial mount before the token is available.
        if (authLoading) return;

        const headers = getAuthHeader();
        if (!headers.Authorization) return;

        fetchProviders();
        fetchHealth();
        // Poll health every 60 seconds
        const interval = setInterval(fetchHealth, 60000);
        return () => clearInterval(interval);
    }, [fetchProviders, fetchHealth, authLoading, getAuthHeader]);

    const isInstalled = useCallback((pid) => {
        return providers.some(p => p.id === pid || p.provider_id === pid);
    }, [providers]);

    const getHealthStatus = useCallback((pid) => {
        return healthStatus[pid]?.status || 'unknown';
    }, [healthStatus]);

    const installation = providerId ? providers.find(p => p.id === providerId || p.provider_id === providerId) : null;

    return {
        installation, providers, customModules, isInstalled, loading, error,
        refetch: fetchProviders,
        healthStatus,
        getHealthStatus,
    };
}
